import { ArrowRight } from "lucide-react";

import { cn } from "@/lib/utils";
import GenerativeContour from "@/components/primitives/GenerativeContour";
import { monogramFor } from "./exploreShared";

interface CompareTrayProps {
  /**
   * The currently-selected orgs (max 4, enforced upstream). Only id + name are
   * read — the tray renders a monogram and a tooltip, nothing more — so it takes
   * this minimal shape rather than a full org, letting the app-level store feed
   * it without carrying every Explore field.
   */
  picked: { id: string; name: string }[];
  onClear: () => void;
  onCompare: () => void;
}

/**
 * Fixed bottom-centre compare pill. Appears with ≥1 selection; the Compare CTA
 * stays disabled until ≥2 are picked.
 */
const CompareTray = ({ picked, onClear, onCompare }: CompareTrayProps) => {
  if (!picked.length) return null;
  const ready = picked.length >= 2;

  return (
    <div
      data-testid="compare-tray"
      data-floating-tray=""
      className="fixed left-1/2 z-50 flex -translate-x-1/2 max-w-[calc(100vw-16px)] items-center gap-2 rounded-pill bg-ink-900 py-2.5 pl-4 pr-3 text-white sm:gap-4 sm:pl-5 shadow-elevated"
      style={{
        bottom:
          "calc(var(--agent-onboarding-banner-h, 0px) + var(--story-bottom-bar-h, 0px) + 22px)",
      }}
    >
      <div className="flex items-center gap-3">
        <span data-testid="compare-tray-count" className="whitespace-nowrap text-[14px] font-semibold tabular-nums">
          {picked.length} selected
        </span>
        {/* The logos are dropped on a phone so four picks still fit the screen. */}
        <span className="hidden min-[440px]:flex">
          {picked.slice(0, 4).map((o, i) => (
            <span
              key={o.id}
              title={o.name}
              className="h-[30px] w-[30px] overflow-hidden rounded-[9px] ring-2 ring-ink-900"
              style={{ marginLeft: i ? -8 : 0, zIndex: 6 - i }}
            >
              <GenerativeContour name={o.name} variant="logo" rings={6} monogram={monogramFor(o.name)} />
            </span>
          ))}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onClear}
          className="h-[38px] rounded-pill border border-white/20 px-3.5 text-[13.5px] font-semibold text-white/70 transition-colors hover:text-white"
        >
          Clear
        </button>
        {/* Test id rather than a name-based locator: this button's accessible
            name is "Compare" once ready, identical to every unselected card
            toggle on the page. */}
        <button
          type="button"
          data-testid="compare-tray-cta"
          onClick={onCompare}
          disabled={!ready}
          className={cn(
            "inline-flex h-[38px] items-center gap-1.5 whitespace-nowrap rounded-pill px-4 text-[13.5px] sm:px-5 font-semibold text-white transition-colors",
            ready ? "bg-teal-soft hover:bg-teal-primary" : "cursor-not-allowed bg-white/20 opacity-60",
          )}
        >
          Compare{ready ? "" : " · pick 2+"}
          <ArrowRight className="h-4 w-4" strokeWidth={2.1} />
        </button>
      </div>
    </div>
  );
};

export default CompareTray;
